// Localização: frontend/src/components/Management/MedicamentoList.js
//Este componente recebe a lista filtrada e as funções de ação como props.
//A busca agora é feita através do SearchContext no HeaderGestão

import React from 'react';
import { useSearch } from '../../context/SearchContext';
import PrimaryButton from '../Common/PrimaryButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import { Pill, SquarePen, Trash2, FileText } from 'lucide-react';

// Componente de Card para Mobile
const MedicamentoCard = ({ medicamento, canEdit, canDelete, onEdit, onDelete }) => {
    return (
        <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-400 group">
            {/* Header do Card */}
            <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100 p-2 sm:p-4">
                <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Ícone do Medicamento */}
                    <div className="flex-shrink-0 mt-0.5">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-emerald-500 flex items-center justify-center shadow-md">
                            <Pill className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                    </div>
                    
                    {/* Nome e Dosagem */}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {medicamento.nome_medicamento}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
                            <span className="font-semibold">Dosagem:</span> {medicamento.dose} {medicamento.unidade_medida}
                        </p>
                    </div>
                </div>
            </div>

            {/* Corpo do Card */}
            <div className="p-2 sm:p-4 space-y-2 sm:space-y-3">
                {/* Instruções */}
                {medicamento.instrucoes && (
                    <div className="flex items-start text-xs sm:text-sm text-gray-600">
                        <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">
                            {medicamento.instrucoes}
                        </span>
                    </div>
                )}

                {/* Ações */}
                <div className="flex items-center justify-end pt-2 sm:pt-3 border-t border-gray-200 space-x-2">
                    {canEdit && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(medicamento);
                            }}
                            className="p-1.5 sm:p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors duration-200"
                            title="Editar"
                        >
                            <SquarePen className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    )}
                    
                    {canDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(medicamento.id);
                            }}
                            className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title="Excluir"
                        >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const MedicamentoList = ({ 
    data, 
    isLoading, 
    canCreate, 
    canEdit, 
    canDelete,
    handleOpenCreateModal, 
    handleOpenEditModal, 
    handleDeleteMedicamento 
}) => {
    // 🎯 Usar SearchContext diretamente
    const { searchTerm } = useSearch();
    
    return (
        <div className="mt-4">
            {/* 🎯 Barra de Pesquisa removida - Agora está no HeaderGestão */}
            <div className="flex justify-end items-center mb-4">
                {canCreate && (
                    <PrimaryButton 
                        onClick={handleOpenCreateModal}
                        className="w-full sm:w-auto"
                    >
                        <span className="hidden sm:inline">+ Cadastrar Novo Medicamento</span>
                        <span className="sm:hidden">+ Novo</span>
                    </PrimaryButton>
                )}
            </div>
            {isLoading ? (
                <LoadingIndicator />
            ) : data.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                    <p className="text-gray-500 text-sm sm:text-lg">
                        {searchTerm ? `Nenhum medicamento encontrado para "${searchTerm}".` : 'Nenhum medicamento cadastrado.'}
                    </p>
                </div>
            ) : (
                <>
                    {/* Cards para Mobile (até md) */}
                    <div className="grid grid-cols-2 md:hidden gap-2 sm:gap-4">
                        {data.map((med) => (
                            <MedicamentoCard
                                key={med.id}
                                medicamento={med}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                onEdit={handleOpenEditModal}
                                onDelete={handleDeleteMedicamento}
                            />
                        ))}
                    </div>

                    {/* Tabela para Desktop (md e acima) */}
                    <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dosagem</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instruções</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data.map((med) => (
                                    <tr key={med.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{med.nome_medicamento}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{med.dose} {med.unidade_medida}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs overflow-hidden truncate">{med.instrucoes || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {canEdit && (
                                                <span 
                                                    onClick={() => handleOpenEditModal(med)} 
                                                    className="text-green-600 hover:text-green-900 cursor-pointer mr-3"
                                                >
                                                    Editar
                                                </span>
                                            )}
                                            {canDelete && (
                                                <span 
                                                    onClick={() => handleDeleteMedicamento(med.id)} 
                                                    className="text-red-600 hover:text-red-900 cursor-pointer"
                                                >
                                                    Excluir
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default MedicamentoList;